import { Component } from '@angular/core';
import { Role } from '../../model/auth/Role';
import { Competition } from '../../model/competition/Competition';
import { CompetitionService } from '../../service/competition.service';
import { NotificationHandler } from '../../helper/notification.handler';
import { MatDialog } from '@angular/material/dialog';
import { ListItemDetailsModalComponent } from '../../common/list-item-details-modal/list-item-details-modal.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  CompetitionDetailsData,
  CompetitionDetailsModalComponent
} from '../competition-details-modal/competition-details-modal.component';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import {
  ListItemDetailsActionInvoke,
  ListItemDetailsData
} from '../../common/list-item-details/list-item-details.component';

interface DetailsData {
  competition: Competition;
}

@Component({
  selector: 'app-competitions-list',
  templateUrl: './competitions-list.component.html',
  styleUrls: ['./competitions-list.component.scss']
})
export class CompetitionsListComponent extends MobileAwareComponent {

  readonly roles = [Role.Admin];

  competitions!: Competition[] | null;

  constructor(private competitionService: CompetitionService,
              private dialog: MatDialog,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    this.loadCompetitions();
  }

  private loadCompetitions() {
    this.competitions = null;

    this.competitionService.getCompetitions().subscribe({
      next: competitions => {
        this.competitions = competitions;
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Competitions fetch has failed message@@notifications-competitions-fetch-failed-error:Competitions fetch has failed`, error);
      }
    });
  }

  createCompetition() {
    this.dialog.open(CompetitionDetailsModalComponent, {
      width: '600px',
      data: {}
    }).afterClosed().subscribe((data: CompetitionDetailsData) => {
      if (data && data.competition) {
        this.loadCompetitions();
      }
    });
  }

  editCompetition(competition: Competition) {
    this.dialog.open(CompetitionDetailsModalComponent, {
      width: '600px',
      data: {
        competition
      }
    }).afterClosed().subscribe((data: CompetitionDetailsData) => {
      if (data && data.competition) {
        this.loadCompetitions();
      }
    });
  }

  deleteCompetition(competition: Competition) {
    const data: DeleteConfirmationData = {
      title: $localize`:Competitions List|Competitions list delete item title@@competitions-list-delete-item-title:Competition Delete`,
      itemType: $localize`:Competitions List|Competitions list delete item type@@competitions-list-delete-item-type:Competition`,
      name: competition.name,
      actionDesc: $localize`:Competitions List|Competitions list delete item prompt@@competitions-list-delete-item-prompt:will be removed from the system and all assigned users`,
      archive: false
    };
    const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
      this.competitionService.delete(competition.id).subscribe({
        next: () => {
          context.modal.deleteHandled();

          this.loadCompetitions();
        },
        error: error => {
          context.modal.deleteHandled($localize`:Notifications|Competition delete has failed message@@notifications-competition-delete-failed-error:Competition delete has failed`, error);
        }
      });
    });
  }

  showDetails(competition: Competition) {
    if (this.mobileView) {
      const data: ListItemDetailsData<DetailsData> = {
        details: [{
          name: $localize`:Competitions List|Competitions list item 'name' title@@competitions-list-item-name-title:Name:`,
          value: competition.name
        }, {
          name: $localize`:Competitions List|Competitions list item 'created at' title@@competitions-list-item-created-at-title:Created At:`,
          value: competition.createdAt,
        }],
        actions: [
          {
            code: 'edit',
            'name': $localize`:General|Edit action label@@general-edit-action-label:Edit`
          },
          {
            code: 'delete',
            'name': $localize`:General|Delete action label@@general-delete-action-label:Delete`
          }
        ],
        component: {competition}
      };
      this.dialog.open(ListItemDetailsModalComponent, {
        width: '600px',
        data: data
      }).afterClosed().subscribe((data: ListItemDetailsActionInvoke<DetailsData>) => {
        if (data) {
          switch (data.action) {
            case 'edit': {
              if (data.component?.competition) {
                this.editCompetition(data.component?.competition);
              }
              break;
            }
            case 'delete': {
              if (data.component?.competition) {
                this.deleteCompetition(data.component?.competition);
              }
              break;
            }
          }
        }
      });
    }
  }
}
